(() => {
  const PROFILE_CONTENT = {
    thermal: {
      title: 'Los sofocos están marcando demasiado tu día',
      body: 'Los cambios repentinos de temperatura están teniendo más peso en tu bienestar. Tu resultado también contempla cómo se relacionan con los sudores nocturnos, la claridad mental y el estado de ánimo.',
      productReason: 'Inira Menopausia reúne cinco extractos vegetales y cuatro micronutrientes en una única rutina diaria para acompañar esta etapa de forma integral.'
    },
    sleep: {
      title: 'Los sudores nocturnos están interrumpiendo tus noches',
      body: 'Despertarte con calor o sudor puede hacer que la noche se vuelva fragmentada y que al día siguiente notes menos claridad o más irritabilidad. Tu resultado pide acompañar el conjunto de cambios propios de esta etapa.',
      productReason: 'Inira Menopausia combina extractos vegetales y micronutrientes en una fórmula diaria pensada para dar apoyo integral a los cambios que se solapan durante esta etapa.'
    },
    energy: {
      title: 'Necesitas recuperar sensación de claridad',
      body: 'La falta de concentración, los olvidos cotidianos o la sensación de niebla mental están ocupando demasiado espacio. Tu resultado los sitúa dentro del conjunto de cambios que estás viviendo.',
      productReason: 'Inira Menopausia reúne extractos vegetales y micronutrientes en una única rutina diaria para acompañar de forma amplia esta etapa.'
    },
    emotional: {
      title: 'Tu equilibrio emocional necesita más apoyo',
      body: 'El estrés, la irritabilidad o los cambios de humor están marcando cómo vives esta etapa. No se trata de exigirte más, sino de reconocer lo que está ocurriendo y construir una rutina constante que contemple el conjunto.',
      productReason: 'Inira Menopausia reúne extractos vegetales y micronutrientes en una fórmula diaria diseñada para acompañar varias áreas de la menopausia al mismo tiempo.'
    },
    integral: {
      title: 'Tu momento pide una solución integral',
      body: 'Sofocos, sudores nocturnos, claridad mental y estado emocional se están solapando. Tu resultado indica que tiene más sentido acompañar el conjunto con una rutina constante que perseguir cada cambio por separado.',
      productReason: 'Inira Menopausia reúne nueve ingredientes en una única fórmula diaria creada para acompañarte de forma amplia y sencilla durante esta etapa.'
    }
  };

  const AXIS_LABELS = {
    thermal: 'Sofocos',
    sleep: 'Sudores nocturnos',
    energy: 'Claridad mental',
    emotional: 'Equilibrio emocional'
  };

  const SCREEN_FLOW = [
    'intro',
    'stage',
    'priority',
    'thermal',
    'insight',
    'impact',
    'sleep',
    'formula',
    'energy',
    'emotional',
    'empathy',
    'duration',
    'summary',
    'analysis',
    'discount',
    'result'
  ];

  const SCREEN_NEXT = Object.fromEntries(SCREEN_FLOW.map((screen, index) => [screen, SCREEN_FLOW[index + 1]]));

  class MenopauseQuiz extends HTMLElement {
    constructor() {
      super();
      this.answers = {};
      this.history = [];
      this.currentScreen = 'intro';
      this.answerTimer = null;
      this.analysisFrame = null;
      this.scratchCleanup = null;
      this.discountCountdownTimer = null;
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.chrome = this.querySelector('[data-quiz-chrome]');
      this.progress = this.querySelector('[role="progressbar"]');
      this.progressValue = this.querySelector('[data-progress-value]');
      this.progressCount = this.querySelector('[data-progress-count]');
      this.progressLabel = this.querySelector('[data-progress-label]');
      this.questionActions = this.querySelector('[data-question-actions]');
      this.questionContinue = this.querySelector('[data-question-continue]');
      this.discountCode = this.dataset.discountCode || 'QUIZ10';

      this.addEventListener('click', this.handleClick.bind(this));
      this.showScreen('intro', { pushHistory: false, focus: false });
    }

    disconnectedCallback() {
      window.clearTimeout(this.answerTimer);
      this.stopAnalysis();
      this.destroyScratchCard();
      this.stopDiscountCountdown();
      this.setScrollLock(false);
    }

    handleClick(event) {
      const answer = event.target.closest('[data-answer]');
      if (answer) {
        this.selectAnswer(answer);
        return;
      }

      if (event.target.closest('[data-question-continue]')) {
        if (this.questionContinue.disabled) return;
        this.advanceFromQuestion();
        return;
      }

      const next = event.target.closest('[data-next]');
      if (next) {
        this.showScreen(next.dataset.next || SCREEN_NEXT[this.currentScreen]);
        return;
      }

      if (event.target.closest('[data-quiz-back]')) {
        this.goBack();
        return;
      }

      if (event.target.closest('[data-show-result]')) {
        this.renderResult();
        this.showScreen('analysis');
        this.startAnalysis();
        this.emitEvent('QuizCompleted');
        return;
      }

      if (event.target.closest('[data-analysis-result]')) {
        this.stopAnalysis();
        this.showScreen('discount', { pushHistory: false });
        this.emitEvent('QuizDiscountUnlocked');
        return;
      }

      const discountContinue = event.target.closest('[data-discount-continue]');
      if (discountContinue) {
        this.applyDiscount(discountContinue);
        return;
      }

      if (event.target.closest('[data-restart]')) {
        this.restart();
      }
    }

    selectAnswer(button) {
      const question = button.dataset.question;
      const screen = button.closest('[data-screen]');
      const previous = screen.querySelector('[data-answer].is-selected');

      if (previous) {
        previous.classList.remove('is-selected');
        previous.setAttribute('aria-pressed', 'false');
      }

      button.classList.add('is-selected');
      button.setAttribute('aria-pressed', 'true');
      this.answers[question] = {
        value: button.dataset.value,
        axis: button.dataset.axis || '',
        score: Number(button.dataset.score || 0)
      };
      this.questionContinue.disabled = false;
    }

    advanceFromQuestion() {
      const nextScreen = this.getNextScreen(this.currentScreen);
      if (nextScreen === 'insight') this.updateInsight();
      if (nextScreen === 'formula') this.updateFormula();
      if (nextScreen === 'summary') this.renderSummary();
      this.showScreen(nextScreen);
    }

    getNextScreen(screen) {
      return SCREEN_NEXT[screen] || 'result';
    }

    showScreen(screenName, options = {}) {
      const { pushHistory = true, focus = true } = options;
      const nextScreen = this.querySelector(`[data-screen="${screenName}"]`);
      if (!nextScreen) return;

      const active = this.querySelector('[data-screen].is-active');
      if (pushHistory && active && active.dataset.screen !== screenName) {
        this.history.push(active.dataset.screen);
      }

      this.querySelectorAll('[data-screen]').forEach((screen) => {
        screen.classList.remove('is-active', 'is-entering');
        screen.hidden = true;
      });

      nextScreen.hidden = false;
      nextScreen.classList.add('is-active', 'is-entering');
      this.currentScreen = screenName;
      const isDiscount = screenName === 'discount';
      const isResult = screenName === 'result';
      const isAnalysis = screenName === 'analysis';
      this.classList.toggle('is-analysis-screen', isAnalysis);
      this.classList.toggle('is-discount-screen', isDiscount);
      this.classList.toggle('is-result-screen', isResult);
      if (isDiscount) {
        this.startDiscountCountdown();
      } else {
        this.stopDiscountCountdown();
      }
      this.setScrollLock(isAnalysis || isDiscount || isResult);
      this.updateChrome(nextScreen);
      this.updateQuestionActions(nextScreen);

      if (screenName === 'stage') this.emitEvent('QuizStarted');
      if (screenName === 'result') this.emitEvent('RecommendationViewed');
      if (isDiscount) {
        window.requestAnimationFrame(() => this.initializeScratchCard());
      } else {
        this.destroyScratchCard();
      }

      if (focus) {
        const heading = nextScreen.querySelector('h1, h2');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
        }
        this.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      }
    }

    updateChrome(screen) {
      const hideChrome = ['intro', 'analysis', 'discount', 'result'].includes(screen.dataset.screen);
      this.chrome.hidden = hideChrome;
      if (hideChrome) return;

      const questionNumber = Number(screen.dataset.questionNumber || this.getCompletedQuestionCount());
      const displayNumber = Math.min(questionNumber, 8);
      const progressPercent = Math.max(0, Math.min(100, (displayNumber / 8) * 100));

      this.progress.setAttribute('aria-valuenow', String(displayNumber));
      this.progressValue.style.width = `${progressPercent}%`;
      if (this.progressCount) this.progressCount.textContent = `${displayNumber}/8`;
      if (this.progressLabel) this.progressLabel.textContent = this.getProgressLabel(displayNumber);
    }

    updateQuestionActions(screen) {
      const isQuestion = screen.hasAttribute('data-question-screen');
      this.questionActions.hidden = !isQuestion;
      if (!isQuestion) return;

      const question = screen.querySelector('[data-answer]')?.dataset.question;
      this.questionContinue.disabled = !question || !this.answers[question];
    }

    getProgressLabel(questionNumber) {
      if (questionNumber <= 2) return 'Tu momento';
      if (questionNumber <= 5) return 'Cómo te está afectando';
      if (questionNumber <= 7) return 'Claridad y emociones';
      return 'Tu guía';
    }

    getCompletedQuestionCount() {
      return Object.keys(this.answers).length;
    }

    goBack() {
      window.clearTimeout(this.answerTimer);
      this.stopAnalysis();
      const previous = this.history.pop();
      if (!previous) return;
      this.showScreen(previous, { pushHistory: false });
    }

    updateInsight() {
      const thermal = Number(this.answers.thermal?.value || 0);
      const priority = this.answers.priority?.value;
      const title = this.querySelector('[data-insight-title]');
      const body = this.querySelector('[data-insight-body]');

      if (thermal >= 2 || priority === 'thermal') {
        title.textContent = 'Tu termostato interno no está fallando';
        body.textContent = 'Los sofocos son habituales durante la transición menopáusica. Entender cuándo aparecen y cuánto interfieren ayuda a construir una rutina más coherente contigo.';
      } else if (priority === 'sleep') {
        title.textContent = 'Los sudores nocturnos no son solo una incomodidad';
        body.textContent = 'Despertarte acalorada o con sudor puede fragmentar la noche y repercutir después en tu concentración, tu tolerancia al estrés y tu estado de ánimo.';
      } else {
        title.textContent = 'La menopausia no se manifiesta igual en todas';
        body.textContent = 'Para algunas mujeres pesan más los sofocos; para otras, la claridad mental, el estrés o los cambios de humor. Tu experiencia no tiene por qué parecerse a la de nadie más.';
      }
    }

    updateFormula() {
      const scores = this.calculateScores();
      const primary = this.getPrimaryAxis(scores);
      const title = this.querySelector('[data-formula-title]');
      const body = this.querySelector('[data-formula-body]');

      const formulaCopy = {
        thermal: ['Menos sofocos y sudores nocturnos es el cambio más reportado', 'El 84% reportó menos sofocos y sudores nocturnos. Inira Menopausia acompaña este cambio como parte de una rutina constante durante esta etapa.'],
        sleep: ['Cuando bajan los sudores nocturnos, la noche también puede cambiar', 'El 84% reportó menos sofocos y sudores nocturnos. Reducir estas interrupciones puede ayudarte a vivir las noches con mayor calma.'],
        energy: ['Recuperar claridad y energía también forma parte del resultado', 'El 79% sintió mayor claridad mental y el 76% notó una mejora en su energía: dos cambios importantes para volver a sentirte presente durante el día.'],
        emotional: ['Sentirte más estable también es un cambio real', 'El 81% experimentó un mejor bienestar emocional. Inira Menopausia plantea una rutina integral para acompañar tanto los cambios físicos como los emocionales.'],
        integral: ['Una rutina pensada para que vuelvas a notar el cambio', 'El 84% reportó menos sofocos y sudores nocturnos, el 79% mayor claridad mental, el 81% mejor bienestar emocional y el 76% más energía.']
      };

      title.textContent = formulaCopy[primary][0];
      body.textContent = formulaCopy[primary][1];
    }

    calculateScores() {
      const scores = { thermal: 0, sleep: 0, energy: 0, emotional: 0 };

      Object.values(this.answers).forEach((answer) => {
        if (answer.axis && Object.prototype.hasOwnProperty.call(scores, answer.axis)) {
          scores[answer.axis] += answer.score;
        }
        if (answer.axis === 'integral') {
          Object.keys(scores).forEach((axis) => {
            scores[axis] += answer.score;
          });
        }
      });

      const priorityAxis = this.answers.priority?.axis;
      const impact = Number(this.answers.impact?.score || 0);
      if (priorityAxis && Object.prototype.hasOwnProperty.call(scores, priorityAxis)) {
        scores[priorityAxis] += impact;
      } else if (priorityAxis === 'integral') {
        Object.keys(scores).forEach((axis) => {
          scores[axis] += Math.ceil(impact / 2);
        });
      }

      return scores;
    }

    getPrimaryAxis(scores) {
      const highAxes = Object.values(scores).filter((score) => score >= 4).length;
      if (this.answers.priority?.value === 'integral' || highAxes >= 3) return 'integral';

      return Object.entries(scores).sort((a, b) => b[1] - a[1])[0]?.[0] || 'integral';
    }

    getAxisLevel(score) {
      if (score >= 6) return 'Prioridad alta';
      if (score >= 3) return 'Prioridad media';
      return 'Prioridad baja';
    }

    renderSummary() {
      const scores = this.calculateScores();
      Object.entries(scores).forEach(([axis, score]) => {
        const output = this.querySelector(`[data-axis-result="${axis}"]`);
        if (output) output.textContent = this.getAxisLevel(score);
      });
    }

    renderResult() {
      const scores = this.calculateScores();
      const profile = this.getPrimaryAxis(scores);
      const content = PROFILE_CONTENT[profile];

      this.querySelector('[data-result-title]').textContent = content.title;
      this.querySelector('[data-result-body]').textContent = content.body;
      this.querySelector('[data-product-reason]').textContent = content.productReason;
    }

    startAnalysis() {
      this.stopAnalysis();

      const progress = this.querySelector('[data-analysis-progress]');
      const progressValue = this.querySelector('[data-analysis-progress-value]');
      const percent = this.querySelector('[data-analysis-percent]');
      const steps = Array.from(this.querySelectorAll('[data-analysis-step]'));
      const resultButton = this.querySelector('[data-analysis-result]');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const duration = reducedMotion ? 900 : 6500;
      const startedAt = performance.now();

      progress.setAttribute('aria-valuenow', '0');
      progressValue.style.width = '0%';
      percent.textContent = '0%';
      resultButton.hidden = true;
      steps.forEach((step) => step.classList.remove('is-active', 'is-complete'));
      steps[0]?.classList.add('is-active');

      const update = (now) => {
        if (this.currentScreen !== 'analysis') return;

        const elapsed = Math.min(duration, now - startedAt);
        const value = Math.min(100, Math.round((elapsed / duration) * 100));
        const activeIndex = Math.min(steps.length - 1, Math.floor((value / 100) * steps.length));

        progress.setAttribute('aria-valuenow', String(value));
        progressValue.style.width = `${value}%`;
        percent.textContent = `${value}%`;

        steps.forEach((step, index) => {
          step.classList.toggle('is-complete', index < activeIndex || value === 100);
          step.classList.toggle('is-active', index === activeIndex && value < 100);
        });

        if (value < 100) {
          this.analysisFrame = window.requestAnimationFrame(update);
          return;
        }

        this.analysisFrame = null;
        resultButton.hidden = false;
        resultButton.focus({ preventScroll: true });
      };

      this.analysisFrame = window.requestAnimationFrame(update);
    }

    stopAnalysis() {
      if (this.analysisFrame !== null) window.cancelAnimationFrame(this.analysisFrame);
      this.analysisFrame = null;
    }

    setScrollLock(locked) {
      document.documentElement.classList.toggle('mq-lock-scroll', locked);
      document.body.classList.toggle('mq-lock-scroll', locked);
    }

    initializeScratchCard() {
      this.destroyScratchCard();

      const card = this.querySelector('[data-scratch-card]');
      const canvas = this.querySelector('[data-scratch-canvas]');
      const hint = this.querySelector('[data-scratch-hint]');
      const continueButton = this.querySelector('[data-discount-continue]');
      if (!card || !canvas || !hint || !continueButton) return;

      const context = canvas.getContext('2d', { willReadFrequently: false });
      if (!context) {
        return;
      }

      let isDrawing = false;
      let isRevealed = false;
      let lastPoint = null;
      const coveredCells = new Set();
      const gridSize = 18;
      const revealThreshold = 0.4;

      const resizeCanvas = () => {
        if (isRevealed) return;
        const rect = card.getBoundingClientRect();
        const ratio = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = Math.max(1, Math.round(rect.width * ratio));
        canvas.height = Math.max(1, Math.round(rect.height * ratio));
        canvas.style.width = `${rect.width}px`;
        canvas.style.height = `${rect.height}px`;
        context.setTransform(ratio, 0, 0, ratio, 0, 0);
        coveredCells.clear();

        const gradient = context.createLinearGradient(0, 0, rect.width, rect.height);
        gradient.addColorStop(0, '#ffe47a');
        gradient.addColorStop(0.52, '#ffc914');
        gradient.addColorStop(1, '#f2b700');
        context.globalCompositeOperation = 'source-over';
        context.fillStyle = gradient;
        context.fillRect(0, 0, rect.width, rect.height);
        context.fillStyle = 'rgba(255, 255, 255, 0.2)';
        for (let index = 0; index < 24; index += 1) {
          const x = (((index * 47) % 97) / 100) * rect.width;
          const y = (((index * 71) % 89) / 92) * rect.height;
          const radius = 0.7 + (index % 3) * 0.45;
          context.beginPath();
          context.arc(x, y, radius, 0, Math.PI * 2);
          context.fill();
        }
      };

      const reveal = () => {
        if (isRevealed) return;
        isRevealed = true;
        isDrawing = false;
        canvas.classList.add('is-revealed');
        hint.hidden = true;
        this.emitEvent('QuizDiscountRevealed');
      };

      const getPoint = (event) => {
        const rect = canvas.getBoundingClientRect();
        return {
          x: Math.max(0, Math.min(rect.width, event.clientX - rect.left)),
          y: Math.max(0, Math.min(rect.height, event.clientY - rect.top)),
          width: rect.width,
          height: rect.height
        };
      };

      const markCoverage = (point, radius) => {
        const startX = Math.max(0, Math.floor((point.x - radius) / gridSize));
        const endX = Math.ceil((point.x + radius) / gridSize);
        const startY = Math.max(0, Math.floor((point.y - radius) / gridSize));
        const endY = Math.ceil((point.y + radius) / gridSize);

        for (let y = startY; y <= endY; y += 1) {
          for (let x = startX; x <= endX; x += 1) {
            const cellX = x * gridSize + gridSize / 2;
            const cellY = y * gridSize + gridSize / 2;
            if (Math.hypot(cellX - point.x, cellY - point.y) <= radius) {
              coveredCells.add(`${x}:${y}`);
            }
          }
        }

        const totalCells = Math.ceil(point.width / gridSize) * Math.ceil(point.height / gridSize);
        if (coveredCells.size / totalCells >= revealThreshold) reveal();
      };

      const scratch = (point) => {
        const radius = Math.max(22, Math.min(34, point.width * 0.075));
        context.globalCompositeOperation = 'destination-out';
        context.lineWidth = radius * 2;
        context.lineCap = 'round';
        context.lineJoin = 'round';
        context.beginPath();
        context.moveTo(lastPoint?.x ?? point.x, lastPoint?.y ?? point.y);
        context.lineTo(point.x, point.y);
        context.stroke();
        context.beginPath();
        context.arc(point.x, point.y, radius, 0, Math.PI * 2);
        context.fill();
        markCoverage(point, radius);
        lastPoint = point;
      };

      const onPointerDown = (event) => {
        if (isRevealed) return;
        isDrawing = true;
        lastPoint = null;
        canvas.setPointerCapture(event.pointerId);
        scratch(getPoint(event));
      };

      const onPointerMove = (event) => {
        if (!isDrawing || isRevealed) return;
        scratch(getPoint(event));
      };

      const stopDrawing = (event) => {
        isDrawing = false;
        lastPoint = null;
        if (event?.pointerId !== undefined && canvas.hasPointerCapture(event.pointerId)) {
          canvas.releasePointerCapture(event.pointerId);
        }
      };

      const onKeyDown = (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        reveal();
      };

      const resizeObserver = new ResizeObserver(resizeCanvas);
      resizeObserver.observe(card);
      canvas.addEventListener('pointerdown', onPointerDown);
      canvas.addEventListener('pointermove', onPointerMove);
      canvas.addEventListener('pointerup', stopDrawing);
      canvas.addEventListener('pointercancel', stopDrawing);
      canvas.addEventListener('keydown', onKeyDown);
      resizeCanvas();

      this.scratchCleanup = () => {
        resizeObserver.disconnect();
        canvas.removeEventListener('pointerdown', onPointerDown);
        canvas.removeEventListener('pointermove', onPointerMove);
        canvas.removeEventListener('pointerup', stopDrawing);
        canvas.removeEventListener('pointercancel', stopDrawing);
        canvas.removeEventListener('keydown', onKeyDown);
      };
    }

    destroyScratchCard() {
      this.scratchCleanup?.();
      this.scratchCleanup = null;
    }

    startDiscountCountdown() {
      this.stopDiscountCountdown();

      const countdown = this.querySelector('[data-discount-countdown]');
      if (!countdown) return;

      const update = () => {
        const now = new Date();
        const midnight = new Date(now);
        midnight.setHours(24, 0, 0, 0);
        const remaining = Math.max(0, midnight.getTime() - now.getTime());
        const totalSeconds = Math.floor(remaining / 1000);
        const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
        const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
        const seconds = String(totalSeconds % 60).padStart(2, '0');
        countdown.textContent = `${hours}:${minutes}:${seconds}`;
        countdown.setAttribute('datetime', `PT${hours}H${minutes}M${seconds}S`);
      };

      update();
      this.discountCountdownTimer = window.setInterval(update, 1000);
    }

    stopDiscountCountdown() {
      if (this.discountCountdownTimer) {
        window.clearInterval(this.discountCountdownTimer);
        this.discountCountdownTimer = null;
      }
    }

    applyDiscount(button) {
      const code = this.discountCode.trim();
      const productUrl = this.dataset.productUrl || '/products/menopausia';

      if (!code) {
        window.location.assign(productUrl);
        return;
      }

      button.disabled = true;
      button.textContent = 'Aplicando descuento...';
      this.emitEvent('QuizDiscountApplied');

      // The editor cannot render Shopify's /discount route in its iframe.
      // Navigate to the product exactly like the live discount handoff does.
      if (window.Shopify?.designMode) {
        window.location.assign(productUrl);
        return;
      }

      const root = window.Shopify?.routes?.root || '/';
      const discountUrl = `${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(productUrl)}`;
      window.location.assign(discountUrl);
    }

    emitEvent(eventName) {
      this.dispatchEvent(new CustomEvent('inira:quiz-event', {
        bubbles: true,
        detail: { name: eventName, quiz: 'menopausia' }
      }));
    }

    restart() {
      window.clearTimeout(this.answerTimer);
      this.stopAnalysis();
      this.answers = {};
      this.history = [];
      this.querySelectorAll('[data-answer]').forEach((answer) => {
        answer.classList.remove('is-selected');
        answer.setAttribute('aria-pressed', 'false');
      });
      this.showScreen('intro', { pushHistory: false });
    }
  }

  if (!customElements.get('menopause-quiz')) {
    customElements.define('menopause-quiz', MenopauseQuiz);
  }
})();
